import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useCatalogProductsQuery,
  useDeleteCatalogProduct,
} from "../api/queries";
import { CatalogProductsFilter } from "../ui/CatalogProductsFilter";
import { CatalogProductsTable } from "../ui/CatalogProductsTable";
import { CatalogProductsToolbar } from "../ui/CatalogProductsToolbar";

type CatalogSearch = {
  limit?: number;
  offset?: number;
  status?: string;
  categoryId?: string;
  brandId?: string;
  type?: string;
  search?: string;
};

export function CatalogProductsPage() {
  const nav = useNavigate({ from: "/app/catalog" });
  const search = useSearch({ from: "/app/catalog" }) as CatalogSearch;

  const list = useCatalogProductsQuery({
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
    status: search.status as
      | "draft"
      | "active"
      | "hidden"
      | "archived"
      | undefined,
    categoryId: search.categoryId,
    brandId: search.brandId,
    type: search.type as
      | "part_new"
      | "part_oem"
      | "part_used"
      | "service"
      | undefined,
    search: search.search,
  });

  const deleteProduct = useDeleteCatalogProduct();
  const canCreate = useActionPermission(["catalog:create"]);
  const canManage = useActionPermission(
    ["catalog:update", "catalog:delete"],
    "any",
  );

  const data = list.data?.data ?? [];
  const meta = list.data?.meta ?? { total: 0, limit: 20, offset: 0 };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Catalog" },
          ]}
        />
      </Header>

      <Main>
        <CatalogProductsToolbar
          onCreate={() => nav({ to: "/app/catalog/create" })}
          canCreate={!!canCreate}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <CatalogProductsFilter />

          <CatalogProductsTable
            canManage={!!canManage}
            isLoading={list.isLoading}
            data={data}
            offset={meta.offset}
            limit={meta.limit}
            totalCount={meta.total}
            onEdit={(row) =>
              nav({ to: "/app/catalog/$id/edit", params: { id: row.id } })
            }
            onDelete={async (id) => {
              try {
                await deleteProduct.mutateAsync(id);
                toast.success("ລຶບສິນຄ້າສຳເລັດ");
              } catch (err: unknown) {
                const msg =
                  err && typeof err === "object" && "message" in err
                    ? String((err as { message: string }).message)
                    : "ມີຮ້ານໃຊ້ສິນຄ້ານີ້ຢູ່ ບໍ່ສາມາດລຶບໄດ້";
                toast.error(msg);
              }
            }}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
          />
        </div>
      </Main>
    </>
  );
}
